import { Check } from "lucide-react";
import { classNames } from "../../helpers/classNames";

/**
 * The five builder steps, in order, as the redesign names them. `short` is
 * what the stepper shows: the full name of step 4 doesn't fit five steps in
 * a row at tablet or desktop width (the tablet and desktop mockups say
 * "Cover and note" for the same reason). The mobile row uses `mobile` when
 * set (step 1 reads "Book title" there), else the full name.
 */
export const BUILDER_STEP_LABELS = [
  { label: "Title", mobile: "Book title" },
  { label: "Cover photo" },
  { label: "Page photos" },
  { label: "Cover and dedication", short: "Cover and note" },
  { label: "Preview" },
];

const asStep = (entry) => (typeof entry === "string" ? { label: entry } : entry);

/**
 * Where you are in the builder (docs/design/HANDOFF.md, "Progress").
 *
 * Mobile: the current step's label, "Step X of N" and a segmented bar whose
 * segments go to their step under the same rules as the stepper.
 * Tablet and desktop: the numbered stepper with labels, checkmarks on done
 * steps, and lines between them. From lg to xl it sits inline in the one-row
 * header with less room, so it keeps the tablet's tighter spacing and never
 * wraps; the roomier desktop spacing starts at xl.
 *
 * `step` is 1-based. `canGoTo(n)` says whether step n is clickable; `onGoTo(n)`
 * is called when it is. The current step is never clickable.
 */
const Progress = ({ step, labels = BUILDER_STEP_LABELS, mobileLabel, canGoTo = () => false, onGoTo = () => {}, className }) => {
  const steps = labels.map(asStep);
  const total = steps.length;
  const current = Math.min(Math.max(step, 1), total);

  return (
    <div className={className}>
      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex justify-between text-[13px] text-md-muted">
          <span className="font-semibold text-md-dark">{mobileLabel || steps[current - 1].mobile || steps[current - 1].label}</span>
          <span>
            Step {current} of {total}
          </span>
        </div>
        {/* Each segment is a step: tappable like the stepper from tablet up. The bar is 4px; the button around it
            is 44px tall and hangs over the space above and below, so the layout doesn't move. */}
        <nav aria-label="Progress" className="mt-2 grid gap-1" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
          {steps.map(({ label }, i) => {
            const n = i + 1;
            const bar = <span className={classNames("block h-1 w-full rounded-sm", n <= current ? "bg-md-pink" : "bg-md-pink-light-3")} />;
            return n !== current && canGoTo(n) ? (
              <button
                key={label}
                type="button"
                onClick={() => onGoTo(n)}
                aria-label={`Go to step ${n}: ${label}`}
                className="-my-5 flex h-11 items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
              >
                {bar}
              </button>
            ) : (
              <span key={label} aria-hidden="true" className="flex items-center">
                {bar}
              </span>
            );
          })}
        </nav>
      </div>

      {/* Tablet and desktop */}
      <nav aria-label="Progress" className="hidden md:flex justify-center">
        <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 lg:flex-nowrap xl:gap-2.5">
          {steps.map(({ label, short }, i) => {
            const n = i + 1;
            const done = n < current;
            const active = n === current;
            const clickable = !active && canGoTo(n);
            const circle = (
              <span
                className={classNames(
                  "flex h-7 w-7 items-center justify-center rounded-full border-2 text-[13px] font-bold",
                  n <= current ? "border-md-pink-dark bg-md-pink-dark text-white" : "border-md-control-input bg-white text-md-muted"
                )}
              >
                {done ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> : n}
              </span>
            );
            const text = (
              <span
                className={classNames("whitespace-nowrap text-[13px] xl:text-sm", active ? "font-bold text-md-dark" : "font-medium text-md-muted")}
                aria-label={short ? label : undefined}
              >
                {short || label}
              </span>
            );
            return (
              <li key={label} className="flex items-center gap-1.5 xl:gap-2.5" aria-current={active ? "step" : undefined}>
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => onGoTo(n)}
                    className="flex min-h-[44px] items-center gap-2 rounded-md px-0.5 xl:gap-2.5 xl:px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
                  >
                    {circle}
                    {text}
                  </button>
                ) : (
                  <span className="flex min-h-[44px] items-center gap-2 px-0.5 xl:gap-2.5 xl:px-1">
                    {circle}
                    {text}
                  </span>
                )}
                {i < total - 1 ? (
                  <span aria-hidden="true" className={classNames("h-0.5 w-4 rounded-sm xl:w-8", done ? "bg-md-pink" : "bg-md-pink-light-3")} />
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Progress;
